import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from "../../../../AuthContext/AuthContext";
import { Helper } from "../../../../tools/Helper";
import { api_Routes } from "../../../../api_Routes";
import { useCenteredSnackbar } from "../../../../tools/useCenteredSnackbar";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Button,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Tabs,
    Tab,
    CircularProgress,
} from '@mui/material';
import {
    Receipt,
    Payment,
    CalendarToday,
    Person,
    Link as LinkIcon,
    Description,
    Image,
    Settings,
    Analytics,
    ViewCarousel,
    PushPin,
    EmojiEvents,
    SupportAgent,
    Security,
    BarChart,
    ConfirmationNumber,
    Send,
    CheckCircle,
    Public,
    Timeline,
} from '@mui/icons-material';

const OrderDetails = () => {
    const { auth } = useAuth();
    const { id } = useParams();
    const [isGetDataLoading, setIsGetDataLoading] = useState(true);
    const [isStatisticsLoading, setIsStatisticsLoading] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [statisticsData, setStatisticsData] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const { showCenteredPopup } = useCenteredSnackbar();

    const fetchServiceData = async () => {
        setIsGetDataLoading(true);
        const { response, message } = await Helper.Get({
            url: `${api_Routes.OurServices.getByUuid}${id}`,
            hasToken: true,
        });

        if (response) {
            setOrderData(response.data);
            // If the service has statistics, fetch them
            if (response.data.pin || response.data.banner || response.data.top_result || response.data.tickets_ads) {
                fetchStatistics(response.data.request_id);
            }
        } else {
            showCenteredPopup(message || "Failed to fetch service data", {
                variant: "error",
                persist: true
            });
        }
        setIsGetDataLoading(false);
    };

    const fetchStatistics = async (requestId) => {
        setIsStatisticsLoading(true);
        const { response, message } = await Helper.Get({
            url: `${api_Routes.dashboard.orderhistory.Statistics}${requestId}`,
            hasToken: true,
        });

        if (response) {
            setStatisticsData(response.data);
        } else {
            console.error("Failed to fetch statistics:", message);
        }
        setIsStatisticsLoading(false);
    };

    useEffect(() => {
        fetchServiceData();
    }, [id]);

    const getStatusChip = (status) => {
        const statusConfig = {
            pending: { color: 'warning', label: 'Pending' },
            paid: { color: 'success', label: 'Paid' },
            new_request: { color: 'info', label: 'New Request' },
            in_progress: { color: 'primary', label: 'In Progress' },
            complete: { color: 'success', label: 'Completed' },
            active: { color: 'success', label: 'Active' },
        };

        const config = statusConfig[status] || { color: 'default', label: status };
        return (
            <Chip
                label={config.label}
                color={config.color}
                variant="filled"
                size="small"
            />
        );
    };

    const getServiceIcon = (serviceId) => {
        const iconMap = {
            1: <Analytics />,
            4: <ConfirmationNumber />, // Bulk-Ticket
            9: <ViewCarousel />,
        };
        return iconMap[serviceId] || <Settings />;
    };

    const getServiceTypeDetails = () => {
        if (!orderData) return null;

        // Check which service type relation exists - using EXACT relation names from Request model
        if (orderData.pin) {
            const startDate = new Date(orderData.pin.date_start_active);
            const days = orderData.our_service_type ?
                orderData.our_service_type.days :
                orderData.pin.count_days_subscriptions;
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + (days || 0));

            return {
                type: 'Pin Service',
                icon: <PushPin />,
                details: [
                    { label: 'Pin URL', value: orderData.pin.pin_url },
                    { label: 'Pin Place', value: orderData.pin.pin_place },
                    { label: 'Subscription Days', value: days || 'N/A' },
                    { label: 'Start Date', value: startDate.toLocaleDateString() },
                    { label: 'End Date', value: endDate.toLocaleDateString() },
                ]
            };
        }

        if (orderData.banner) {
            // Calculate end date
            const startDate = new Date(orderData.banner.date_start_active);
            let endDate;

            if (orderData.our_service_type) {
                // If our_service_type exists, add its days
                endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + (orderData.our_service_type.days || 0));
            } else {
                // If our_service_type is null, add the count
                endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + (orderData.count || 0));
            }

            return {
                type: 'Banner Service',
                icon: <Image />,
                details: [
                    { label: 'Start Date', value: startDate.toLocaleDateString() },
                    { label: 'End Date', value: endDate.toLocaleDateString() },
                    { label: 'Subscription Days', value: orderData.our_service_type ?
                            (orderData.our_service_type.days || 'N/A') :
                            (orderData.count || 'N/A')
                    },
                ]
            };
        }

        if (orderData.top_result) {
            const startDate = new Date(orderData.top_result.date_start_active);
            const days = orderData.our_service_type ?
                orderData.our_service_type.days :
                orderData.top_result.count_days_subscriptions;
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + (days || 0));

            return {
                type: 'Top Result Service',
                icon: <EmojiEvents />,
                details: [
                    { label: 'Start Date', value: startDate.toLocaleDateString() },
                    { label: 'End Date', value: endDate.toLocaleDateString() },
                    { label: 'Subscription Days', value: days || 'N/A' },
                ]
            };
        }

        if (orderData.tickets_ads) {
            const startDate = new Date(orderData.tickets_ads.date_start_active);
            const days = orderData.our_service_type ?
                orderData.our_service_type.days :
                orderData.tickets_ads.count_days_subscriptions;
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + (days || 0));

            const completionRate = orderData.tickets_ads.count_ticket_subscriptions > 0
                ? ((orderData.tickets_ads.count_ticket_send / orderData.tickets_ads.count_ticket_subscriptions) * 100).toFixed(1)
                : 0;

            return {
                type: 'Tickets Ads Service',
                icon: <ConfirmationNumber />,
                details: [
                    { label: 'Subject', value: orderData.tickets_ads.subject },
                    { label: 'Total Tickets', value: orderData.tickets_ads.count_ticket_subscriptions },
                    { label: 'Tickets Sent', value: orderData.tickets_ads.count_ticket_send },
                    { label: 'Remaining Tickets', value: orderData.tickets_ads.count_ticket_subscriptions - orderData.tickets_ads.count_ticket_send },
                    { label: 'Start Date', value: startDate.toLocaleDateString() },
                    { label: 'End Date', value: endDate.toLocaleDateString() },
                    { label: 'Subscription Days', value: days || 'N/A' },
                    { label: 'Completion Rate', value: `${completionRate}%` },
                ]
            };
        }

        if (orderData.trust) {
            const startDate = new Date(orderData.trust.date_start_active);
            const days = orderData.our_service_type ?
                orderData.our_service_type.days :
                orderData.trust.count_days_subscriptions;
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + (days || 0));

            return {
                type: 'Trust Service',
                icon: <Security />,
                details: [
                    { label: 'Start Date', value: startDate.toLocaleDateString() },
                    { label: 'End Date', value: endDate.toLocaleDateString() },
                    { label: 'Subscription Days', value: days || 'N/A' },
                ]
            };
        }

        return {
            type: orderData.our_service?.name || 'General Service',
            icon: getServiceIcon(orderData.our_service_id),
            details: []
        };
    };

    const renderStatisticsTab = () => {
        if (isStatisticsLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!statisticsData) {
            return (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Typography color="text.secondary">
                        No statistics available for this service
                    </Typography>
                </Box>
            );
        }

        // Special rendering for TicketsAds statistics
        if (orderData.tickets_ads) {
            return renderTicketsAdsStatistics();
        }

        // Regular statistics for other services (pin, banner, top_result)
        return (
            <Grid container spacing={3}>
                {/* Overall Statistics */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <BarChart sx={{ mr: 1 }} />
                                Overall Performance
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                                        <Typography variant="h4" color="primary" fontWeight="bold">
                                            {statisticsData.overall_statistics?.total_views || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Views
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6}>
                                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e8' }}>
                                        <Typography variant="h4" color="success.main" fontWeight="bold">
                                            {statisticsData.overall_statistics?.total_clicks || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Clicks
                                        </Typography>
                                    </Paper>
                                </Grid>
                                {/*<Grid item xs={6}>*/}
                                {/*    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>*/}
                                {/*        <Typography variant="h4" color="warning.main" fontWeight="bold">*/}
                                {/*            {statisticsData.overall_statistics?.total_reply || 0}*/}
                                {/*        </Typography>*/}
                                {/*        <Typography variant="body2" color="text.secondary">*/}
                                {/*            Total Replies*/}
                                {/*        </Typography>*/}
                                {/*    </Paper>*/}
                                {/*</Grid>*/}
                                <Grid item xs={6}>
                                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fce4ec' }}>
                                        <Typography variant="h4" color="secondary" fontWeight="bold">
                                            {statisticsData.overall_statistics?.total_clicks_and_views || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Interactions
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Device Statistics */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                Device Distribution
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Computer: {statisticsData.statisticsUsedDevices?.percentage_computer?.toFixed(1) || 0}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={statisticsData.statisticsUsedDevices?.percentage_computer || 0}
                                    sx={{ height: 10, borderRadius: 5, mb: 2 }}
                                />

                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Phone: {statisticsData.statisticsUsedDevices?.percentage_phone?.toFixed(1) || 0}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={statisticsData.statisticsUsedDevices?.percentage_phone || 0}
                                    sx={{ height: 10, borderRadius: 5, mb: 2 }}
                                    color="success"
                                />

                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Other: {statisticsData.statisticsUsedDevices?.percentage_other?.toFixed(1) || 0}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={statisticsData.statisticsUsedDevices?.percentage_other || 0}
                                    sx={{ height: 10, borderRadius: 5 }}
                                    color="secondary"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Country Statistics */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <Public sx={{ mr: 1 }} />
                                Geographic Distribution
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Country</TableCell>
                                            <TableCell align="right">Percentage</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {statisticsData.countryStatistics?.map((country, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{country.country || 'Unknown'}</TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={`${country.percentage}%`}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {(!statisticsData.countryStatistics || statisticsData.countryStatistics.length === 0) && (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center">
                                                    <Typography color="text.secondary">
                                                        No country data available
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Daily Statistics */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <Timeline sx={{ mr: 1 }} />
                                Daily Performance
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <TableContainer sx={{ maxHeight: 300 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell align="right">Views</TableCell>
                                            <TableCell align="right">Clicks</TableCell>
                                            <TableCell align="right">Replies</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {statisticsData.dateAndTotalviewsOrClicks?.views?.slice(0, 10).map((day, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{new Date(day.date).toLocaleDateString()}</TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={day.total}
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={statisticsData.dateAndTotalviewsOrClicks?.clicks?.[index]?.total || 0}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={statisticsData.dateAndTotalviewsOrClicks?.replys?.[index]?.total || 0}
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {(!statisticsData.dateAndTotalviewsOrClicks?.views || statisticsData.dateAndTotalviewsOrClicks.views.length === 0) && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    <Typography color="text.secondary">
                                                        No daily data available
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    const renderTicketsAdsStatistics = () => {
        const ticketStats = statisticsData.ticket_stats || {};
        const completionRate = ticketStats.completion_rate ||
            (orderData.tickets_ads.count_ticket_subscriptions > 0
                ? ((orderData.tickets_ads.count_ticket_send / orderData.tickets_ads.count_ticket_subscriptions) * 100).toFixed(1)
                : 0);

        return (
            <Grid container spacing={3}>
                {/* Ticket Campaign Performance */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <ConfirmationNumber sx={{ mr: 1 }} />
                                Ticket Campaign Performance
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={3}>
                                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                                        <ConfirmationNumber sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                        <Typography variant="h4" color="primary" fontWeight="bold">
                                            {ticketStats.total_tickets || orderData.tickets_ads.count_ticket_subscriptions}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Tickets
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e8' }}>
                                        <Send sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                                        <Typography variant="h4" color="success.main" fontWeight="bold">
                                            {ticketStats.tickets_sent || orderData.tickets_ads.count_ticket_send}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Tickets Sent
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
                                        <CheckCircle sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                                        <Typography variant="h4" color="warning.main" fontWeight="bold">
                                            {completionRate}%
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Completion Rate
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#fce4ec' }}>
                                        <Typography variant="h4" color="secondary" fontWeight="bold">
                                            {ticketStats.remaining_tickets ||
                                            (orderData.tickets_ads.count_ticket_subscriptions - orderData.tickets_ads.count_ticket_send)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Remaining Tickets
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Progress Bar */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Campaign Progress: {completionRate}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={completionRate}
                                    sx={{ height: 10, borderRadius: 5 }}
                                    color={completionRate >= 100 ? "success" : completionRate >= 50 ? "primary" : "warning"}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Campaign Details */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Campaign Details
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Subject
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {orderData.tickets_ads.subject}
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Panel URL
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            <a
                                                href={orderData.tickets_ads.panel_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#1976d2', textDecoration: 'none' }}
                                            >
                                                {orderData.tickets_ads.panel_url}
                                            </a>
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Start Date
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {new Date(orderData.tickets_ads.date_start_active).toLocaleDateString()}
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Subscription Days
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {orderData.tickets_ads.count_days_subscriptions} days
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    const renderDetailsTab = () => {
        const serviceTypeInfo = getServiceTypeDetails();

        return (
            <Grid container spacing={3}>
                {/* Order Information */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <Receipt sx={{ mr: 1 }} />
                                Order Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <Description />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Description"
                                        secondary={orderData.description || 'No description provided'}
                                    />
                                </ListItem>

                                <ListItem>
                                    <ListItemIcon>
                                        <LinkIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Panel URL"
                                        secondary={
                                            <a href={orderData.panel_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                                {orderData.panel_url}
                                            </a>
                                        }
                                    />
                                </ListItem>

                                <ListItem>
                                    <ListItemIcon>
                                        <CalendarToday />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Created Date"
                                        secondary={new Date(orderData.created_at).toLocaleString()}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Payment & Pricing */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <Payment sx={{ mr: 1 }} />
                                Payment & Pricing
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell><strong>Base Price</strong></TableCell>
                                            <TableCell align="right">${orderData.price}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell><strong>Discount</strong></TableCell>
                                            <TableCell align="right">
                                                {orderData.discount_type === 'percentage'
                                                    ? `${orderData.discount}%`
                                                    : orderData.discount_type === 'const'
                                                        ? `$${orderData.discount}`
                                                        : 'No discount'
                                                }
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell><strong>Quantity/Count</strong></TableCell>
                                            <TableCell align="right">{orderData.count}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Service Specific Details */}
                {serviceTypeInfo.details.length > 0 && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    {serviceTypeInfo.icon}
                                    <Box sx={{ ml: 1 }}>{serviceTypeInfo.type} Details</Box>
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                <Grid container spacing={2}>
                                    {serviceTypeInfo.details.map((detail, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Paper variant="outlined" sx={{ p: 2 }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    {detail.label}
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {detail.value || 'N/A'}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Service Information */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Service Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {orderData.our_service ? (
                                <List dense>
                                    <ListItem>
                                        <ListItemText
                                            primary="Service Name"
                                            secondary={orderData.our_service.name}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText
                                            primary="Description"
                                            secondary={orderData.our_service.description}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText
                                            primary="Source"
                                            secondary={orderData.our_service.source}
                                        />
                                    </ListItem>

                                </List>
                            ) : (
                                <Typography color="text.secondary">
                                    No service information available
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Additional Information */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Additional Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <List dense>
                                {orderData.logo && (
                                    <ListItem>
                                        <ListItemText
                                            primary="Logo"
                                            secondary={
                                                <Box sx={{ mt: 1 }}>
                                                    <img
                                                        src={orderData.logo}
                                                        alt="Service Logo"
                                                        style={{
                                                            maxWidth: '100%',
                                                            maxHeight: 100,
                                                            borderRadius: 4,
                                                            border: '1px solid #ddd'
                                                        }}
                                                    />
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                )}

                                <ListItem>
                                    <ListItemText
                                        primary="Discount Type"
                                        secondary={orderData.discount_type || 'None'}
                                    />
                                </ListItem>

                                {orderData.payment_method && (
                                    <ListItem>
                                        <ListItemText
                                            primary="Payment Method"
                                            secondary={orderData.payment_method}
                                        />
                                    </ListItem>
                                )}

                                {orderData.paid_at && (
                                    <ListItem>
                                        <ListItemText
                                            primary="Paid At"
                                            secondary={new Date(orderData.paid_at).toLocaleString()}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (isGetDataLoading) {
        return (
            <Box sx={{ width: '100%', p: 3 }}>
                <LinearProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>Loading order details...</Typography>
            </Box>
        );
    }

    if (!orderData) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="error">
                    Order not found or failed to load.
                </Typography>
                <Button variant="contained" onClick={fetchServiceData} sx={{ mt: 2 }}>
                    Retry
                </Button>
            </Box>
        );
    }

    const serviceTypeInfo = getServiceTypeDetails();
    const hasStatistics = orderData.pin || orderData.banner || orderData.top_result || orderData.tickets_ads;

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
            {/* Header */}
            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 60, height: 60 }}>
                                {serviceTypeInfo.icon}
                            </Avatar>
                        </Grid>
                        <Grid item xs>
                            <Typography variant="h4" fontWeight="bold">
                                {orderData.title || 'Order Details'}
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                {serviceTypeInfo.type} • Request ID: {orderData.request_id}
                            </Typography>
                        </Grid>
                        <Grid item>
                            {getStatusChip(orderData.status)}
                            <Box sx={{ mt: 1 }}>
                                {getStatusChip(orderData.status_payment)}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Card sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange} centered>
                    <Tab label="Order Details" />
                    {hasStatistics && <Tab label="Statistics & Analytics" />}
                </Tabs>
            </Card>

            {/* Tab Content */}
            {activeTab === 0 && renderDetailsTab()}
            {activeTab === 1 && hasStatistics && renderStatisticsTab()}

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                {orderData.status_payment === 'pending' && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => window.open(`/ServicesInvoice?invoice_id=${orderData.uuid}`, '_blank')}
                    >
                        Pay Now
                    </Button>
                )}

                {orderData.tickets_ads?.report && (
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => window.open(orderData.tickets_ads.report, '_blank')}
                    >
                        Download Report
                    </Button>
                )}

                <Button
                    variant="outlined"
                    onClick={() => window.history.back()}
                >
                    Back to Orders
                </Button>
            </Box>
        </Box>
    );
};

export default OrderDetails;